import { Button } from '@/components/ui/button';
import {
  CheckCircle, ArrowRight, WhatsappLogo, Envelope, FileText,
} from '@phosphor-icons/react';
import { CONTACT } from '@/lib/constants';

// Generic, non-assessment-specific consultation hrefs for landing CTAs.
// (The pre-filled assessment-scoped hrefs live in the results dashboard.)
const GENERIC_REPORT_REQUEST_WHATSAPP = `https://wa.me/${CONTACT.WHATSAPP_RAW}?text=${encodeURIComponent(
`Hello Aurea Digital Solutions.

I would like to request the AWSAI Professional Diagnostic Report.

Organisation:
Contact Person:
Email:
Phone:
Requested Service: Professional Diagnostic Report

Please advise on the next steps.`
)}`;

const GENERIC_REPORT_REQUEST_EMAIL = `mailto:${CONTACT.EMAIL}?subject=${encodeURIComponent(
  'AWSAI Professional Report Request'
)}&body=${encodeURIComponent(
`Dear Aurea Digital Solutions,

I would like to request the AWSAI Professional Diagnostic Report.

Organisation:
Contact Person:
Email:
Phone:
Requested Service: Professional Diagnostic Report

Please advise on the next steps.

Thank you.`
)}`;

// ---------- Free vs Professional comparison ----------

const FREE_FEATURES = [
  'Workflow Stability Score',
  'Basic maturity level',
  'Domain-level snapshot',
  'AI readiness snapshot',
  'Online results dashboard',
  'High-level recommendation',
];

const PRO_FEATURES = [
  '30+ page Professional Diagnostic Report',
  'Executive Summary',
  'Consultant\u2019s Overall Assessment',
  'Key Message for Management',
  'AWSAI Benchmark Analysis',
  'Benchmark position and comparative assessment',
  'Domain-by-domain diagnostic analysis',
  'AI Opportunity Matrix',
  'SMART-Workflow transformation roadmap',
  'Management Priority Matrix',
  'Potential next steps',
  'Formal report reference number',
];

const PricingSection = ({ onStart }) => (
  <section id="pricing" className="py-20 lg:py-28 bg-white" data-testid="pricing-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          What You Receive
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-4">
          Two Ways to Start
        </h2>
        <p className="text-slate-600 leading-relaxed text-base">
          Begin with the free diagnostic to understand your workflow stability and AI readiness,
          or request the Professional Diagnostic Report for a formal consulting-grade analysis
          suitable for management review.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
        {/* FREE */}
        <div data-testid="pricing-card-free" className="border-2 border-slate-200 bg-white rounded-xl p-7 lg:p-9 flex flex-col shadow-sm">
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-slate-500 mb-2">Tier 1</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Free Assessment</h3>
          <div className="text-3xl font-black text-slate-900 tracking-tight mb-1">Free</div>
          <div className="text-xs text-slate-500 mb-6">Online — completes in ~10 minutes</div>
          <ul className="space-y-2.5 mb-8 flex-1">
            {FREE_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                <CheckCircle size={18} weight="fill" className="text-teal-600 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button
            data-testid="pricing-cta-free"
            onClick={onStart}
            size="lg"
            className="w-full bg-slate-900 text-white hover:bg-slate-800 font-semibold"
          >
            Start Free Assessment
            <ArrowRight size={18} weight="bold" className="ml-2" />
          </Button>
        </div>

        {/* PROFESSIONAL */}
        <div
          data-testid="pricing-card-pdf-report"
          className="relative border-2 border-amber-300 bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 text-white rounded-xl p-7 lg:p-9 flex flex-col shadow-2xl"
        >
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] tracking-[0.2em] font-bold rounded-full uppercase bg-amber-300 text-slate-900">
            Recommended
          </span>
          <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-amber-300 mb-2">Tier 2 · Consulting-Grade</div>
          <h3 className="text-xl font-bold text-white mb-2">Professional Diagnostic Report</h3>
          <div className="flex items-baseline gap-2 mb-1">
            <div className="text-3xl font-black text-amber-300 tracking-tight">RM 499</div>
            <div className="text-xs text-slate-400">per assessment</div>
          </div>
          <div className="text-xs text-slate-300 mb-6">
            Formal consulting-grade report for management review and decision-making
          </div>
          <ul className="space-y-2.5 mb-8 flex-1">
            {PRO_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-100">
                <CheckCircle size={18} weight="fill" className="text-amber-300 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300 font-bold mb-2">
            Available Upon Consultation
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button asChild size="lg" className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold">
              <a
                href={GENERIC_REPORT_REQUEST_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-cta-pdf-report-whatsapp"
              >
                <WhatsappLogo size={18} weight="fill" className="mr-2" />
                WhatsApp Aurea
              </a>
            </Button>
            <Button asChild size="lg" className="flex-1 bg-amber-300 text-slate-900 hover:bg-amber-200 font-semibold">
              <a href={GENERIC_REPORT_REQUEST_EMAIL} data-testid="pricing-cta-pdf-report-email">
                <Envelope size={18} weight="bold" className="mr-2" />
                Email Aurea
              </a>
            </Button>
          </div>
          <div className="text-[11px] text-slate-400 mt-3 leading-relaxed">
            Online payment is temporarily disabled during the launch quality-assurance period.
            Aurea Digital Solutions will respond promptly to confirm next steps and deliver the report.
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Professional Report Preview ----------

const PREVIEW_SECTIONS = [
  { title: 'Executive Summary', desc: 'Headline findings and CEO briefing box in a single page.' },
  { title: 'Consultant\u2019s Overall Assessment', desc: 'Integrated narrative interpretation of the score and posture.' },
  { title: 'Benchmark Position', desc: 'Where your organisation stands against four AWSAI benchmark groups.' },
  { title: 'Domain Diagnostic', desc: 'Per-domain findings: what they mean, what happens if nothing changes.' },
  { title: 'AI Opportunity Matrix', desc: 'Workflow-by-workflow automation potential and recommended technologies.' },
  { title: 'SMART-Workflow Roadmap', desc: '30-day quick wins and 90-day transformation phases.' },
  { title: 'Management Priority Matrix', desc: 'Where to focus management attention first.' },
  { title: 'Potential Next Steps', desc: 'Engagement options scoped through consultation.' },
];

export const ReportPreviewSection = () => (
  <section id="report-preview" className="py-20 lg:py-28 bg-slate-50" data-testid="report-preview-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          Inside the Professional Diagnostic Report
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-5">
          Preview the Professional Diagnostic Report
        </h2>
        <p className="text-slate-600 leading-relaxed text-base">
          The Professional Diagnostic Report is designed for management review and decision-making.
          It translates assessment results into benchmarked findings, operational implications, and
          practical improvement priorities.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PREVIEW_SECTIONS.map((s, idx) => (
          <div
            key={s.title}
            className="bg-white border border-slate-200 rounded-lg p-5 hover:border-teal-400 hover:shadow-md transition-all"
          >
            <div className="text-[10px] tracking-[0.2em] font-bold text-teal-700 mb-1">SECTION {idx + 1}</div>
            <div className="text-[15px] font-bold text-slate-900 mb-1.5">{s.title}</div>
            <p className="text-xs text-slate-600 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-start">
        <Button asChild size="lg" className="bg-slate-900 text-white hover:bg-slate-800 font-semibold">
          <a href={GENERIC_REPORT_REQUEST_EMAIL} data-testid="preview-request-button">
            <FileText size={18} weight="bold" className="mr-2" />
            Request Sample Report
          </a>
        </Button>
        <div className="text-xs text-slate-500 max-w-md">
          Aurea Digital Solutions can share representative pages from the consulting-grade
          deliverable on request.
        </div>
      </div>
    </div>
  </section>
);

export default PricingSection;
